import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { extractContractFromFile } from '../../../services/extractContract';


// Error Boundary for Modal Submissions
class ModalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    console.error('[ModalErrorBoundary] Error caught:', error);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ModalErrorBoundary] Component error:', {
      error: error?.toString(),
      errorInfo: errorInfo,
      componentStack: errorInfo?.componentStack
    });
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state?.hasError) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl mx-4 shadow-xl">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Icon name="alert-circle" className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Modal Error Detected
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  An error occurred in the modal component:
                </p>
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                  <p className="text-xs font-mono text-red-800">
                    {this.state?.error?.toString()}
                  </p>
                </div>
                <p className="text-xs text-destructive/70">
                  💡 Tip: Open browser console (F12) to see detailed error logs
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                  this.props?.onClose?.();
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Close Modal
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props?.children;
  }
}

const AddOfferModal = ({ isOpen, onClose, onSubmit, existingOffer = null }) => {
  const DEBUG_OCR = import.meta.env?.DEV || false;
  
  const [formData, setFormData] = useState(existingOffer || {
    buyerName: '',
    offerPrice: '',
    downPayment: '',
    financingType: 'Conventional',
    earnestMoney: '',
    closingDate: '',
    contingencies: [],
    contingencyNotes: {},
    sellerConcessions: '',
    buyerAgent: '',
    preApprovalAmount: '',
    escalationClause: '',
    internalNotes: '',
    balanceOfPurchase: '',
    buyersAgentCommission: ''
  });

  // New state for OCR mapping feedback
  const [ocrMappingInfo, setOcrMappingInfo] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Update form data when existingOffer changes (for edit mode)
  useEffect(() => {
    if (existingOffer) {
      setFormData({
        buyerName: existingOffer?.buyerName || '',
        offerPrice: existingOffer?.offerPrice?.toString() || '',
        downPayment: existingOffer?.downPayment?.toString() || '',
        financingType: existingOffer?.financingType || 'Conventional',
        earnestMoney: existingOffer?.earnestMoney?.toString() || '',
        closingDate: existingOffer?.closingDate || '',
        contingencies: existingOffer?.contingencies || [],
        contingencyNotes: existingOffer?.contingencyNotes || {},
        sellerConcessions: existingOffer?.sellerConcessions?.toString() || '',
        buyerAgent: existingOffer?.buyerAgent || '',
        preApprovalAmount: existingOffer?.preApprovalAmount?.toString() || '',
        escalationClause: existingOffer?.escalationClause || '',
        internalNotes: existingOffer?.internalNotes || '',
        balanceOfPurchase: existingOffer?.balanceOfPurchase?.toString() || '',
        buyersAgentCommission: existingOffer?.buyersAgentCommission?.toString() || ''
      });
    } else {
      // Reset form when creating new offer
      setFormData({
        buyerName: '',
        offerPrice: '',
        downPayment: '',
        financingType: 'Conventional',
        earnestMoney: '',
        closingDate: '',
        contingencies: [],
        contingencyNotes: {},
        sellerConcessions: '',
        buyerAgent: '',
        preApprovalAmount: '',
        escalationClause: '',
        internalNotes: '',
        balanceOfPurchase: '',
        buyersAgentCommission: ''
      });
    }
  }, [existingOffer]);

  const [errors, setErrors] = useState({});
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [parseSuccess, setParseSuccess] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [detailedErrorLog, setDetailedErrorLog] = useState(null);
  
  // PDF Viewer State
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileObject, setPdfFileObject] = useState(null); // Store actual File object for upload
  const [pdfScale, setPdfScale] = useState(1.0);
  const [isDragging, setIsDragging] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  // Add this block - Missing state variables for extraction progress tracking
  const [extractionProgress, setExtractionProgress] = useState({ status: 'idle', current: 0, total: 6, message: '' });
  const [extractionRequestId, setExtractionRequestId] = useState(null);
  const [extractionDuration, setExtractionDuration] = useState(null);

  const financingOptions = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Conventional', label: 'Conventional' },
    { value: 'FHA', label: 'FHA' },
    { value: 'VA', label: 'VA' },
    { value: 'Other', label: 'Other' }
  ];

  const contingencyOptions = [
    'Inspection',
    'Appraisal',
    'Financing',
    'Sale of Current Home',
    'Title Review',
    'HOA Review'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleContingencyToggle = (contingency) => {
    setFormData(prev => ({
      ...prev,
      contingencies: prev?.contingencies?.includes(contingency)
        ? prev?.contingencies?.filter(c => c !== contingency)
        : [...prev?.contingencies, contingency]
    }));
  };

  const handleContingencyNoteChange = (contingency, note) => {
    setFormData(prev => ({
      ...prev,
      contingencyNotes: {
        ...prev?.contingencyNotes,
        [contingency]: note
      }
    }));
  };

  // Add this block - Missing cancel extraction handler
  const handleCancelExtraction = () => {
    if (DEBUG_OCR) console.log('[PDF Extraction] Cancelling extraction...');
    setIsParsing(false);
    setExtractionProgress({ status: 'idle', current: 0, total: 6, message: '' });
    setParseError('Extraction cancelled by user');
  };

  /**
   * Helper: Convert value to ISO date string (YYYY-MM-DD) or empty string
   * @param {any} value - Date value (string, Date, or null/undefined)
   * @returns {string} ISO date string or empty string
   */
  const toISODateSafe = (value) => {
    if (!value) return '';
    
    // If already in YYYY-MM-DD format, return as-is
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/?.test(value)) {
      return value;
    }
    
    // Try to parse as date
    try {
      const date = new Date(value);
      if (isNaN(date?.getTime())) return '';
      
      // Format as YYYY-MM-DD
      const year = date?.getFullYear();
      const month = String(date?.getMonth() + 1)?.padStart(2, '0');
      const day = String(date?.getDate())?.padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  /**
   * Helper: Convert value to number or null
   * @param {any} value - Numeric value (string, number, or null/undefined)
   * @returns {number|null} Parsed number or null
   */
  const toNumberSafe = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return value;
    
    if (typeof value === 'string') {
      // Remove currency symbols, commas, spaces
      const cleaned = value?.replace(/[$,\s]/g, '')?.trim();
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : parsed;
    }
    
    return null;
  };

  /**
   * Map extracted fields from ExtractedV2 nested structure to flat offer form state
   * @param {Object} extracted - ExtractedV2 data with nested structure:
   *   - propertyAddress: string|null
   *   - buyerNames: string[]
   *   - sellerNames: string[]
   *   - buyerAgent: { name, brokerage, phone, email }
   *   - financing: { type, details, mortgageAmount }
   *   - purchasePrice: number|null
   *   - deposits: { initialDeposit, additionalDeposit, depositTotal, whoHoldsDeposit }
   *   - balanceDueAtClosing: number|null
   *   - dates: { contractDate, closingDate }
   *   - commissions: { buyerAgentCommissionPercent, buyerAgentCommissionAmount, notes }
   *   - contingencies: [{ type, present, deadline, details }]
   *   - evidence, pageMap, confidence, notes
   * @returns {Object} Flat mapped object matching formData keys
   */
  const applyExtractedToOfferForm = (extracted) => {
    const mapped = {};

    // Property Address (direct mapping)
    if (extracted?.propertyAddress) {
      mapped.propertyAddress = extracted?.propertyAddress;
    }

    // Buyer Name (from buyerNames array - join multiple buyers)
    if (extracted?.buyerNames && Array.isArray(extracted?.buyerNames) && extracted?.buyerNames?.length > 0) {
      mapped.buyerName = extracted?.buyerNames?.filter(Boolean)?.join(', ');
    }

    // Seller Name (from sellerNames array - join multiple sellers)
    if (extracted?.sellerNames && Array.isArray(extracted?.sellerNames) && extracted?.sellerNames?.length > 0) {
      mapped.sellerName = extracted?.sellerNames?.filter(Boolean)?.join(', ');
    }

    // Buyer Agent (from buyerAgent object)
    if (extracted?.buyerAgent?.name) {
      mapped.buyerAgent = extracted?.buyerAgent?.name;
    }

    // Financing Type (from financing object)
    if (extracted?.financing?.type) {
      mapped.financingType = extracted?.financing?.type;
    }

    // Purchase Price (direct mapping with safe conversion)
    const purchasePrice = toNumberSafe(extracted?.purchasePrice);
    if (purchasePrice !== null) {
      mapped.offerPrice = purchasePrice;
    }

    // Initial Deposit (from deposits object)
    const initialDeposit = toNumberSafe(extracted?.deposits?.initialDeposit);
    if (initialDeposit !== null) {
      mapped.downPayment = initialDeposit;
    }

    // Additional Deposit (from deposits object)
    const additionalDeposit = toNumberSafe(extracted?.deposits?.additionalDeposit);
    if (additionalDeposit !== null) {
      mapped.earnestMoney = additionalDeposit;
    }

    // Mortgage Amount (from financing object)
    const mortgageAmount = toNumberSafe(extracted?.financing?.mortgageAmount);
    if (mortgageAmount !== null) {
      mapped.preApprovalAmount = mortgageAmount;
    }

    // Balance Due at Closing (direct mapping with safe conversion)
    const balanceDue = toNumberSafe(extracted?.balanceDueAtClosing);
    if (balanceDue !== null) {
      mapped.balanceOfPurchase = balanceDue;
    }

    // Closing Date (from dates object with ISO conversion)
    const closingDate = toISODateSafe(extracted?.dates?.closingDate);
    if (closingDate) {
      mapped.closingDate = closingDate;
    }

    // Commission (from commissions object - prefer percent, fallback to amount)
    const commissionPercent = toNumberSafe(extracted?.commissions?.buyerAgentCommissionPercent);
    if (commissionPercent !== null) {
      mapped.buyersAgentCommission = commissionPercent;
    } else {
      const commissionAmount = toNumberSafe(extracted?.commissions?.buyerAgentCommissionAmount);
      if (commissionAmount !== null) {
        mapped.buyersAgentCommission = commissionAmount;
      }
    }

    // Contingencies (from contingencies array)
    if (extracted?.contingencies && Array.isArray(extracted?.contingencies) && extracted?.contingencies?.length > 0) {
      const contingencyList = [];
      const contingencyNotesObj = {};
      
      extracted?.contingencies?.forEach(cont => {
        if (cont?.type && cont?.present) {
          contingencyList?.push(cont?.type);
          
          // Build note from details and deadline
          const noteParts = [];
          if (cont?.deadline) {
            noteParts?.push(`Deadline: ${toISODateSafe(cont?.deadline)}`);
          }
          if (cont?.details) {
            noteParts?.push(cont?.details);
          }
          
          if (noteParts?.length > 0) {
            contingencyNotesObj[cont.type] = noteParts?.join(' - ');
          }
        }
      });
      
      if (contingencyList?.length > 0) {
        mapped.contingencies = contingencyList;
        mapped.contingencyNotes = contingencyNotesObj;
      }
    }

    // Contract Date (store in internal notes if present)
    const contractDate = toISODateSafe(extracted?.dates?.contractDate);
    if (contractDate) {
      mapped.internalNotes = `Contract Date: ${contractDate}`;
    }

    // Add extraction notes if present
    if (extracted?.notes) {
      const existingNotes = mapped?.internalNotes || '';
      mapped.internalNotes = existingNotes ? `${existingNotes}\n\nExtraction Notes: ${extracted?.notes}` : `Extraction Notes: ${extracted?.notes}`;
    }

    return mapped;
  };

  const handlePDFUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      await processPDFFile(file);
    }
  };

  const processPDFFile = async (file) => {
    if (DEBUG_OCR) console.log('[PDF Upload] Processing file:', file?.name);

    // Validate file type
    if (file?.type !== 'application/pdf') {
      setParseError('Please upload a valid PDF file');
      return;
    }

    // Store file for display
    setPdfFile(URL.createObjectURL(file));
    setPdfFileObject(file);

    // Reset states
    setParseError('');
    setParseSuccess('');
    setExtractedData(null);
    setDetailedErrorLog(null);
    setOcrMappingInfo(null);

    // Start parsing immediately with Vision OCR
    if (DEBUG_OCR) console.log('[PDF → Full Document Extraction] Starting OCR → Text → Extract...');
    setIsParsing(true);

    try {
      const result = await extractContractFromFile(file, {
        onProgress: (message) => {
          if (DEBUG_OCR) console.log('[PDF Extraction Progress]', message);
          // You can update UI with progress messages if needed
        }
      });

      // Handle extraction failure
      if (!result?.success) {
        const errorMsg = result?.message || result?.error || 'Unknown error';

        setParseError(
          `Contract extraction failed. ${errorMsg}. See console diagnostics for details.`
        );
        setIsParsing(false);
        setDetailedErrorLog(result);
        return;
      }

      // ===== STEP 1: Normalize extracted data =====
      // Edge function returns: { success: true, data: { extracted: {...} } }
      const extracted = result?.data?.extracted ?? result?.extracted ?? {};
      
      if (DEBUG_OCR) {
        console.log('[OCR Success] Extracted keys:', Object.keys(extracted));
        console.log('[OCR Success] Full extracted data:', extracted);
      }

      // Store raw extracted data
      setExtractedData(extracted);

      // ===== STEP 2: Apply mapping function =====
      const mapped = applyExtractedToOfferForm(extracted);
      
      if (DEBUG_OCR) {
        console.log('[OCR Mapping] Mapped keys:', Object.keys(mapped));
        console.log('[OCR Mapping] Mapped values:', mapped);
        console.log('[OCR Mapping] Key fields:', {
          address: mapped?.propertyAddress,
          buyer: mapped?.buyerName,
          seller: mapped?.sellerName,
          price: mapped?.offerPrice,
          initialDeposit: mapped?.downPayment,
          additionalDeposit: mapped?.earnestMoney,
          mortgageAmount: mapped?.preApprovalAmount,
          balanceDue: mapped?.balanceOfPurchase,
          closingDate: mapped?.closingDate,
          commission: mapped?.buyersAgentCommission,
          contingencies: mapped?.contingencies
        });
      }

      // ===== STEP 3: Diagnostic log before setting state =====
      if (DEBUG_OCR) console.log('[OCR Mapping] mapped values to set:', mapped);

      // ===== STEP 4: Update form state ONCE =====
      setFormData(prev => ({
        ...prev,
        ...mapped
      }));

      // ===== STEP 5: Prepare UI feedback =====
      const mappedCount = Object.keys(mapped)?.length;
      const mappedFieldNames = Object.keys(mapped);
      
      // Determine which top-level extracted keys were not mapped
      const extractedTopLevelKeys = Object.keys(extracted);
      const unmappedKeys = extractedTopLevelKeys?.filter(key => {
        // These are metadata fields, not data fields
        if (['evidence', 'pageMap', 'confidence', 'notes']?.includes(key)) {
          return false;
        }
        return true;
      });

      setOcrMappingInfo({
        mappedCount,
        mappedFieldNames,
        unmappedKeys,
        extractedKeys: extractedTopLevelKeys,
        mapped,
        buyerAgent: extracted?.buyerAgent // Store buyer agent info for display
      });

      setParseSuccess(`Contract fields extracted successfully! Mapped ${mappedCount} fields.`);
      setIsParsing(false);

    } catch (error) {
      console.error('[PDF → Full Document Extraction] Unexpected error:', error);
      setParseError(`Unexpected error: ${error?.message}`);
      setIsParsing(false);
      setDetailedErrorLog({ error: error?.message, stack: error?.stack });
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      const file = files?.[0];
      await processPDFFile(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.offerPrice || parseFloat(formData?.offerPrice) <= 0) {
      newErrors.offerPrice = 'Valid offer price is required';
    }

    if (formData?.downPayment && parseFloat(formData?.downPayment) < 0) {
      newErrors.downPayment = 'Initial deposit cannot be negative';
    }

    if (formData?.downPayment && parseFloat(formData?.downPayment) > parseFloat(formData?.offerPrice)) {
      newErrors.downPayment = 'Initial deposit cannot exceed offer price';
    }

    if (!formData?.closingDate) {
      newErrors.closingDate = 'Closing date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    const isValid = validateForm();
    
    if (isValid) {
      // Financial validation check
      const offerPrice = parseFloat(formData?.offerPrice || 0);
      const initialDeposit = parseFloat(formData?.downPayment || 0);
      const additionalDeposit = parseFloat(formData?.earnestMoney || 0);
      const balanceOfPurchase = parseFloat(formData?.balanceOfPurchase || 0);
      const mortgageAmount = parseFloat(formData?.preApprovalAmount || 0);
      const sellerConcessions = parseFloat(formData?.sellerConcessions || 0);
      
      const totalSum = initialDeposit + additionalDeposit + balanceOfPurchase + mortgageAmount - sellerConcessions;
      const difference = Math.abs(totalSum - offerPrice);
      
      // Check if the sum equals the total purchase price (with small tolerance for floating point)
      if (Math.abs(totalSum - offerPrice) > 0.01) {
        setShowValidationError(true);
        return;
      }
      
      const offerData = {
        ...formData,
        offerPrice: parseFloat(formData?.offerPrice),
        downPayment: parseFloat(formData?.downPayment || 0),
        earnestMoney: parseFloat(formData?.earnestMoney || 0),
        sellerConcessions: formData?.sellerConcessions ? parseFloat(formData?.sellerConcessions) : 0,
        preApprovalAmount: formData?.preApprovalAmount ? parseFloat(formData?.preApprovalAmount) : 0,
        balanceOfPurchase: parseFloat(formData?.balanceOfPurchase || 0),
        buyersAgentCommission: formData?.buyersAgentCommission ? parseFloat(formData?.buyersAgentCommission) : null
      };

      // If there's a PDF file to upload and we're creating a new offer
      if (pdfFileObject && !existingOffer) {
        offerData.pdfFile = pdfFileObject;
      }

      try {
        setIsSubmitting(true);
        await onSubmit(offerData);
      } catch (error) {
        console.error('[AddOfferModal] Error in onSubmit:', error);
        setIsSubmitting(false);
      }
    }
  };

  const handleCancelPreview = () => {
    setExtractedData(null);
    setParseSuccess('');
    setOcrMappingInfo(null);
  };

  const handleApplyExtractedData = () => {
    console.log('[PDF → OpenAI Extraction] User confirmed - applying extracted data to form');
    
    if (!extractedData?.data) {
      console.error('[PDF → OpenAI Extraction] No extracted data available');
      return;
    }
    
    const data = extractedData?.data;
    
    // Map extracted fields to form fields
    const updates = {};
    
    // Agent → buyerAgent (note: the extracted "agent" is actually the listing agent, but we'll map it to buyerAgent for now)
    if (data?.agent) {
      updates.buyerAgent = data?.agent;
    }
    
    // Offer Price
    if (data?.offerPrice) {
      updates.offerPrice = data?.offerPrice?.toString();
    }
    
    // Closing Date
    if (data?.closingDate) {
      updates.closingDate = data?.closingDate;
    }
    
    // Financing Type
    if (data?.financingType) {
      updates.financingType = data?.financingType;
    }
    
    // Map initialDeposit from PDF to downPayment form field (Initial Deposit)
    if (data?.initialDeposit) {
      updates.downPayment = data?.initialDeposit?.toString();
    }
    
    // Map additionalDeposit from PDF to earnestMoney form field (Additional Deposit)
    if (data?.additionalDeposit) {
      updates.earnestMoney = data?.additionalDeposit?.toString();
    }
    
    // Provisions → internalNotes
    if (data?.provisions) {
      updates.internalNotes = data?.provisions;
    }
    
    // Apply updates to form
    setFormData(prev => ({ ...prev, ...updates }));
    
    // Show success message
    setParseSuccess('Extracted data applied to form! Please review and edit as needed.');
    setExtractedData(null);
    
    console.log('[PDF → OpenAI Extraction] Applied updates:', updates);
  };

  const handleConfirmParsing = async () => {
    console.log('[PDF Autofill] User confirmed - attempting basic autofill from extracted text');
    console.log('[PDF Autofill] NOTE: This is optional autofill only. User must review and edit values.');
    console.log('[PDF Autofill] WARNING: PDF text may be corrupted (e.g., DocuSign overlays). Do NOT trust as source of truth.');
    
    // For now, just close the preview and let user manually enter data
    // In the future, could add basic pattern matching for autofill
    setParseSuccess('PDF text extracted. Please review and manually enter offer details below. The extracted text may contain errors.');
    setExtractedData(null);
    
    // TODO: Could add basic regex pattern matching here for optional autofill
    // But remember: this is OPTIONAL and UNTRUSTED - user must review all values
  };

  const handlePdfViewerClose = () => {
    if (pdfFile) {
      URL.revokeObjectURL(pdfFile);
    }
    setPdfFile(null);
    setPdfFileObject(null);
    setPdfScale(1.0);
  };

  const zoomIn = () => {
    setPdfScale(prev => Math.min(prev + 0.2, 2.0));
  };

  const zoomOut = () => {
    setPdfScale(prev => Math.max(prev - 0.2, 0.6));
  };

  if (!isOpen) {
    return null;
  }

  const DEBUG = import.meta.env?.DEV || false;

  return (
    <ModalErrorBoundary onClose={onClose}>
      <>
        {/* Validation Error Modal */}
        {showValidationError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Icon name="alert-circle" className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Validation Error
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Please check Purchase price/deposit/mortgage amounts
                  </p>
                  <p className="text-xs text-destructive/70 mt-2">
                    💡 Tip: Open browser console (F12) to see detailed error logs
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={() => setShowValidationError(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Main Modal */}
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-card rounded-lg shadow-elevation-2xl max-h-[90vh] overflow-hidden flex flex-col ${
            pdfFile ? 'w-full max-w-[95vw]' : 'w-full max-w-4xl'
          }`}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                {existingOffer ? 'Edit Offer' : 'Add New Offer'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                iconName="X"
              />
            </div>

            {/* PDF Upload Section - Add proper padding */}
            <div className="p-4 md:p-6 space-y-4 border-b border-border">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-foreground">
                  Upload Contract PDF (Optional)
                </label>
                {pdfFile && (
                  <button
                    type="button"
                    onClick={() => {
                      setPdfFile(null);
                      setPdfFileObject(null);
                      setParseError('');
                      setParseSuccess('');
                      setExtractedData(null);
                      setExtractionProgress({ status: 'idle', current: 0, total: 6, message: '' });
                    }}
                    className="text-sm text-destructive hover:text-destructive/80"
                  >
                    Remove PDF
                  </button>
                )}
              </div>

              {/* Progress UI */}
              {isParsing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-blue-900 mb-2">
                          OfferSense.AI is reading and extracting the fields from this contract. Please wait.
                        </p>
                        {extractionProgress?.current > 0 && (
                          <p className="text-xs text-blue-700 mt-1">
                            Step {extractionProgress?.current} of {extractionProgress?.total}
                            {extractionRequestId && (
                              <span className="ml-2 font-mono">ID: {extractionRequestId?.slice(-8)}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelExtraction}
                      className="flex-shrink-0 ml-3 px-3 py-1 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-600 h-2 transition-all duration-300 ease-out"
                      style={{ width: `${(extractionProgress?.current / extractionProgress?.total) * 100}%` }}
                    ></div>
                  </div>

                  {/* Status Messages */}
                  <div className="text-xs text-blue-700 space-y-1">
                    {extractionProgress?.status === 'validating_session' && (
                      <p>🔐 Validating authentication...</p>
                    )}
                    {extractionProgress?.status === 'loading_pdfjs' && (
                      <p>📚 Loading PDF library...</p>
                    )}
                    {extractionProgress?.status === 'loading_pdf' && (
                      <p>📄 Loading PDF file...</p>
                    )}
                    {extractionProgress?.status === 'rendering' && (
                      <p>🖼️ Rendering page {extractionProgress?.current - 2}/{extractionProgress?.total - 4}...</p>
                    )}
                    {extractionProgress?.status === 'uploading' && (
                      <p>☁️ Uploading to extractor...</p>
                    )}
                    {extractionProgress?.status === 'extracting' && (
                      <p>🤖 Extracting contract fields...</p>
                    )}
                  </div>
                </div>
              )}

              {/* Success Message with Extracted Fields */}
              {!isParsing && parseSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="check-circle" className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-green-800 mb-2">Extraction Successful!</p>
                      <p className="text-sm text-green-700 mb-3">{parseSuccess}</p>
                      
                      {/* OCR Mapping Banner */}
                      {ocrMappingInfo && (
                        <div className="mt-3 space-y-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-semibold text-blue-800">
                                📊 Mapped {ocrMappingInfo?.mappedCount} fields from OCR
                              </p>
                              {ocrMappingInfo?.unmappedKeys?.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                >
                                  {showDebugPanel ? '▼' : '▶'} Debug Info
                                </button>
                              )}
                            </div>
                            
                            {/* Show mapped field names */}
                            {ocrMappingInfo?.mappedFieldNames && ocrMappingInfo?.mappedFieldNames?.length > 0 && (
                              <div className="text-xs text-blue-700 space-y-1">
                                <p className="font-medium">Populated fields:</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {ocrMappingInfo?.mappedFieldNames?.map(key => (
                                    <span key={key} className="bg-blue-100 px-2 py-0.5 rounded text-blue-800">
                                      {key}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Buyer Agent Info Display (if available) */}
                            {ocrMappingInfo?.buyerAgent?.name && (
                              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-start space-x-2">
                                  <Icon name="user-check" className="h-4 w-4 text-green-600 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-green-900">
                                      Buyer Agent: {ocrMappingInfo?.buyerAgent?.name}
                                    </p>
                                    {ocrMappingInfo?.buyerAgent?.brokerage && (
                                      <p className="text-xs text-green-700 mt-0.5">
                                        Brokerage: {ocrMappingInfo?.buyerAgent?.brokerage}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Display Key Extracted Fields from ExtractedV2 Structure */}
                      {extractedData && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs font-semibold text-green-800 uppercase tracking-wide">Extracted Fields:</p>
                          <div className="bg-white border border-green-200 rounded p-3 space-y-1.5 max-h-48 overflow-y-auto">
                            {/* Buyer(s) */}
                            {extractedData?.buyerNames && Array.isArray(extractedData?.buyerNames) && extractedData?.buyerNames?.length > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Buyer(s):</span>
                                <span className="text-gray-900">{extractedData?.buyerNames?.filter(Boolean)?.join(', ')}</span>
                              </div>
                            )}
                            
                            {/* Buyer Agent */}
                            {extractedData?.buyerAgent?.name && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Buyer Agent:</span>
                                <span className="text-gray-900">{extractedData?.buyerAgent?.name}</span>
                              </div>
                            )}
                            
                            {/* Financing Type */}
                            {extractedData?.financing?.type && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Financing Type:</span>
                                <span className="text-gray-900">{extractedData?.financing?.type}</span>
                              </div>
                            )}
                            
                            {/* Purchase Price */}
                            {extractedData?.purchasePrice !== null && extractedData?.purchasePrice !== undefined && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Purchase Price:</span>
                                <span className="text-gray-900">${Number(extractedData?.purchasePrice)?.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {/* Initial Deposit */}
                            {extractedData?.deposits?.initialDeposit !== null && extractedData?.deposits?.initialDeposit !== undefined && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Initial Deposit:</span>
                                <span className="text-gray-900">${Number(extractedData?.deposits?.initialDeposit)?.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {/* Additional Deposit */}
                            {extractedData?.deposits?.additionalDeposit !== null && extractedData?.deposits?.additionalDeposit !== undefined && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Additional Deposit:</span>
                                <span className="text-gray-900">${Number(extractedData?.deposits?.additionalDeposit)?.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {/* Mortgage Amount */}
                            {extractedData?.financing?.mortgageAmount !== null && extractedData?.financing?.mortgageAmount !== undefined && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Mortgage Amount:</span>
                                <span className="text-gray-900">${Number(extractedData?.financing?.mortgageAmount)?.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {/* Balance Due at Closing */}
                            {extractedData?.balanceDueAtClosing !== null && extractedData?.balanceDueAtClosing !== undefined && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Balance Due at Closing:</span>
                                <span className="text-gray-900">${Number(extractedData?.balanceDueAtClosing)?.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {/* Closing Date */}
                            {extractedData?.dates?.closingDate && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Closing Date:</span>
                                <span className="text-gray-900">{extractedData?.dates?.closingDate}</span>
                              </div>
                            )}
                            
                            {/* Commission */}
                            {(extractedData?.commissions?.buyerAgentCommissionPercent !== null && extractedData?.commissions?.buyerAgentCommissionPercent !== undefined) && (
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-gray-700">Commission:</span>
                                <span className="text-gray-900">{extractedData?.commissions?.buyerAgentCommissionPercent}%</span>
                              </div>
                            )}
                            
                            {/* Contingencies */}
                            {extractedData?.contingencies && Array.isArray(extractedData?.contingencies) && extractedData?.contingencies?.length > 0 && (
                              <div className="flex flex-col text-xs mt-2 pt-2 border-t border-green-200">
                                <span className="font-medium text-gray-700 mb-1">Contingencies:</span>
                                <div className="space-y-1">
                                  {extractedData?.contingencies?.filter(c => c?.present)?.map((cont, idx) => (
                                    <div key={idx} className="text-gray-900 text-xs">
                                      • {cont?.type}{cont?.deadline ? ` (Deadline: ${cont?.deadline})` : ''}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {extractionDuration && (
                        <p className="text-xs text-green-700 mt-2">
                          Completed in {extractionDuration}s
                          {extractionRequestId && (
                            <span className="ml-2 font-mono">ID: {extractionRequestId?.slice(-8)}</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {!isParsing && parseError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="alert-circle" className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-red-800">Extraction Failed</p>
                      <p className="text-sm text-red-700 mt-1">{parseError}</p>
                      {extractionDuration && (
                        <p className="text-xs text-red-600 mt-2">
                          Failed after {extractionDuration}s
                          {extractionRequestId && (
                            <span className="ml-2 font-mono">ID: {extractionRequestId?.slice(-8)}</span>
                          )}
                        </p>
                      )}
                      {detailedErrorLog && import.meta.env?.DEV && (
                        <details className="mt-3">
                          <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800 font-medium">
                            🔧 Technical Details (Dev Mode)
                          </summary>
                          <pre className="mt-2 text-xs bg-red-100 p-3 rounded overflow-auto max-h-60 border border-red-200">
                            {JSON.stringify(detailedErrorLog, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="FileText" size={20} className="text-primary" />
                    <h3 className="text-base font-semibold text-foreground">Upload Contract PDF</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    We'll attempt to pre-fill key fields from the contract to save time. Because many contracts are electronically signed, some values may not be readable and will require review.
                  </p>
                </div>
                <div
                  className={`flex items-center justify-center gap-3 p-6 border-2 border-dashed rounded-lg transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted/50'
                  } ${isParsing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !isParsing && document.getElementById('pdf-upload')?.click()}
                >
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handlePDFUpload}
                    disabled={isParsing}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <div className="flex flex-col items-center gap-2">
                    <Icon
                      name={isParsing ? "Loader2" : isDragging ? "Download" : "Upload"}
                      size={24}
                      className={`${isParsing ? 'animate-spin' : ''} ${isDragging ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">
                        {isParsing ? 'Parsing...' : isDragging ? 'Drop PDF here' : 'Click or drag PDF'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {!isParsing && !isDragging && 'Max 10MB'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Parse Error Message */}
              {/* REMOVED: Old error message - now using comprehensive error UI above */}
            </div>

            {/* Split View: PDF Viewer + Form */}
            <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
              {/* PDF Viewer Panel */}
              {pdfFile && (
                <div className="w-full md:w-1/2 border-b md:border-b-0 md:border-r border-border bg-muted/10 flex flex-col">
                  {/* PDF Controls */}
                  <div className="p-3 border-b border-border bg-background flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={18} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">Contract PDF</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Zoom Controls */}
                      <button
                        onClick={zoomOut}
                        disabled={pdfScale <= 0.6}
                        className="p-1.5 hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        type="button"
                        title="Zoom Out"
                      >
                        <Icon name="ZoomOut" size={18} />
                      </button>
                      <span className="text-xs text-muted-foreground min-w-[3rem] text-center">
                        {Math.round(pdfScale * 100)}%
                      </span>
                      <button
                        onClick={zoomIn}
                        disabled={pdfScale >= 2.0}
                        className="p-1.5 hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        type="button"
                        title="Zoom In"
                      >
                        <Icon name="ZoomIn" size={18} />
                      </button>
                      
                      {/* Close PDF Viewer */}
                      <button
                        onClick={handlePdfViewerClose}
                        className="p-1.5 hover:bg-muted rounded transition-colors ml-2 pl-2 border-l border-border"
                        type="button"
                        title="Close PDF Viewer"
                      >
                        <Icon name="X" size={18} />
                      </button>
                    </div>
                  </div>
                  
                  {/* PDF Document */}
                  <div className="flex-1 overflow-auto p-4 flex flex-col items-center gap-4 bg-gray-100">
                    {pdfFile ? (
                      <iframe
                        src={pdfFile}
                        className="w-full h-full min-h-[600px] border-0 bg-white shadow-lg"
                        title="Contract PDF Viewer"
                        style={{
                          transform: `scale(${pdfScale})`,
                          transformOrigin: 'top center',
                          width: pdfScale !== 1 ? `${100 / pdfScale}%` : '100%'
                        }}
                      />
                    ) : (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Loader2" size={20} className="animate-spin" />
                        <span>Loading PDF...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Form Panel */}
              <div className={`flex-1 flex flex-col overflow-hidden ${
                pdfFile ? 'w-full md:w-1/2' : 'w-full'
              }`}>
                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
                  {/* AI Extraction Status - Show at top when PDF is loaded */}
                  {pdfFile && (
                    <div className="mb-6">
                      {/* REMOVED: Old isParsing, parseSuccess, and parseError messages */}
                      {/* All status messages are now shown in the PDF Upload Section above */}
                    </div>
                  )}

                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Buyer Name"
                        type="text"
                        placeholder="Enter buyer's full name"
                        value={formData?.buyerName}
                        onChange={(e) => handleInputChange('buyerName', e?.target?.value)}
                        error={errors?.buyerName}
                      />

                      <Input
                        label="Buyer's Agent"
                        type="text"
                        placeholder="Enter agent's name"
                        value={formData?.buyerAgent}
                        onChange={(e) => handleInputChange('buyerAgent', e?.target?.value)}
                      />
                    </div>
                  </div>

                  {/* Terms */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Terms</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Left Column */}
                      <div className="space-y-4">
                        <Input
                          label="Total Purchase Price"
                          type="text"
                          placeholder="0"
                          value={formData?.offerPrice}
                          onChange={(e) => handleInputChange('offerPrice', e?.target?.value)}
                          error={errors?.offerPrice}
                          required
                        />

                        <Input
                          label="Initial Deposit"
                          type="text"
                          placeholder="0"
                          value={formData?.downPayment}
                          onChange={(e) => handleInputChange('downPayment', e?.target?.value)}
                          error={errors?.downPayment}
                        />

                        <Input
                          label="Additional Deposit"
                          type="text"
                          placeholder="0"
                          value={formData?.earnestMoney}
                          onChange={(e) => handleInputChange('earnestMoney', e?.target?.value)}
                        />

                        <Input
                          label="Mortgage Amount"
                          type="text"
                          placeholder="0"
                          value={formData?.preApprovalAmount}
                          onChange={(e) => handleInputChange('preApprovalAmount', e?.target?.value)}
                        />

                        <Input
                          label="Balance due at Purchase"
                          type="text"
                          placeholder="0"
                          value={formData?.balanceOfPurchase}
                          onChange={(e) => handleInputChange('balanceOfPurchase', e?.target?.value)}
                        />

                        <Input
                          label="Seller Concessions"
                          type="text"
                          placeholder="0"
                          value={formData?.sellerConcessions}
                          onChange={(e) => handleInputChange('sellerConcessions', e?.target?.value)}
                        />
                      </div>

                      {/* Right Column */}
                      <div className="space-y-4">
                        <Select
                          label="Financing Type"
                          options={financingOptions}
                          value={formData?.financingType}
                          onChange={(value) => handleInputChange('financingType', value)}
                          required
                        />

                        <Input
                          label="Buyer's Agent Commission (%)"
                          type="text"
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          max="3"
                          value={formData?.buyersAgentCommission}
                          onChange={(e) => {
                            let value = e?.target?.value;
                            // Validate: must be between 0 and 3
                            if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 3)) {
                              handleInputChange('buyersAgentCommission', value);
                            }
                          }}
                          error={errors?.buyersAgentCommission}
                        />

                        <Input
                          label="Closing Date"
                          type="date"
                          value={formData?.closingDate}
                          onChange={(e) => handleInputChange('closingDate', e?.target?.value)}
                          error={errors?.closingDate}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contingencies */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Contingencies</h3>
                    <div className="space-y-4">
                      {contingencyOptions?.map((contingency) => (
                        <div key={contingency}>
                          <Checkbox
                            label={contingency}
                            checked={formData?.contingencies?.includes(contingency)}
                            onChange={() => handleContingencyToggle(contingency)}
                          />
                          {formData?.contingencies?.includes(contingency) && (
                            <div className="mt-2 ml-6">
                              <textarea
                                value={formData?.contingencyNotes?.[contingency] || ''}
                                onChange={(e) => handleContingencyNoteChange(contingency, e?.target?.value)}
                                placeholder={`Add notes for ${contingency}...`}
                                className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Additional Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Escalation Clause
                        </label>
                        <textarea
                          value={formData?.escalationClause}
                          onChange={(e) => handleInputChange('escalationClause', e?.target?.value)}
                          placeholder="Describe any escalation clause terms..."
                          className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Internal Notes
                        </label>
                        <textarea
                          value={formData?.internalNotes}
                          onChange={(e) => handleInputChange('internalNotes', e?.target?.value)}
                          placeholder="Add any internal notes about this offer..."
                          className="w-full min-h-[100px] px-3 py-2 bg-background border border-input rounded-lg text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer - Outside scrollable area */}
                <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border bg-muted/30 flex-shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      onClose();
                    }}
                  >
                    Cancel
                  </Button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:pointer-events-none"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isParsing}
                  >
                    <Icon name="Check" size={16} />
                    {isSubmitting ? 'Submitting...' : (existingOffer ? 'Update Offer' : 'Add Offer')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </ModalErrorBoundary>
  );
};

export default AddOfferModal;